import { useCallback, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";

const SOCKET_URL = process.env.REACT_APP_WEBRTC_SERVER_URL || "https://webrtc-01.lodos.io";

type ConnectionStatus = "connecting" | "connected" | "disconnected" | "error";
type CallSignalType =
  | "offer"
  | "answer"
  | "ice-candidate"
  | "media-state"
  | "media-state-request"
  | "hand-state"
  | "hand-state-request"
  | "screen-share-state"
  | "screen-share-state-request";

interface IceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

interface CallUser {
  odaHakları: string;
  nickname: string;
}

interface SignalingHandlers {
  onConnected?: (iceServers: IceServer[]) => void;
  onCallStarted?: (data: { callId: string; roomId: string; startedBy: CallUser }) => void;
  onUserJoined?: (data: { callId: string; user: CallUser; participantCount: number }) => void;
  onUserLeft?: (data: { callId: string; user: CallUser; remainingCount: number }) => void;
  onCallEnded?: (data: { callId: string; reason: string }) => void;
  onSignal?: (data: { from: string; fromNickname: string; signalType: string; data: unknown }) => void;
  onMediaChanged?: (data: { user: CallUser; mediaType: string; enabled: boolean }) => void;
  // Fired after the socket auto-rejoins an active call following a reconnect.
  // The participants list is the fresh roster from the server's call-start
  // ACK — consumers should use it to rebuild stale peer connections.
  onReconnectedToCall?: (data: { callId: string; participants: CallUser[] }) => void;
}

interface UseSocketConfig {
  token?: string | null;
  autoConnect?: boolean;
  handlers?: SignalingHandlers;
}

interface UseSocketReturn {
  socket: Socket | null;
  status: ConnectionStatus;
  roomId: string | null;
  callId: string | null;
  userId: string | null;
  iceServers: IceServer[];
  connect: (token?: string) => void;
  disconnect: () => void;
  setNickname: (nickname: string) => void;
  joinRoom: (roomId: string, ack?: (data: unknown) => void) => void;
  leaveRoom: () => void;
  startCall: (roomId: string) => Promise<{ callId: string; participants: CallUser[] } | null>;
  leaveCall: () => void;
  sendSignal: (to: string, signalType: CallSignalType, data: unknown) => void;
  toggleMedia: (mediaType: "audio" | "video", enabled: boolean) => void;
}

export function useSocket(config: UseSocketConfig = {}): UseSocketReturn {
  const { token, autoConnect = false, handlers = {} } = config;

  const socketRef = useRef<Socket | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>("disconnected");
  const [roomId, setRoomId] = useState<string | null>(null);
  const [callId, setCallId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [iceServers, setIceServers] = useState<IceServer[]>([]);
  const callIdRef = useRef<string | null>(null);
  // Intent refs — what the user asked to join, persisted across reconnects
  const intentRoomIdRef = useRef<string | null>(null);
  const intentCallActiveRef = useRef<boolean>(false);
  const intentNicknameRef = useRef<string | null>(null);
  const hasHadFirstConnectRef = useRef<boolean>(false);

  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const connect = useCallback((authToken?: string) => {
    const tokenToUse = authToken || token;

    if (!tokenToUse) {
      console.warn("[useSocket] Missing auth token. Pass `token` via config or call connect(token).");
      setStatus("error");
      return;
    }

    if (socketRef.current) {
      try {
        socketRef.current.auth = { token: tokenToUse };
        if (!socketRef.current.connected) {
          setStatus("connecting");
          socketRef.current.connect();
        }
      } catch {
        // ignore
      }
      return;
    }

    setStatus("connecting");

    const socket = io(SOCKET_URL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      auth: { token: tokenToUse },
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setUserId(socket.id ?? null);
    });

    socket.on("connected", (data: { iceServers?: IceServer[] }) => {
      setStatus("connected");
      if (data.iceServers) {
        setIceServers(data.iceServers);
        handlersRef.current.onConnected?.(data.iceServers);
      }

      // Auto-rejoin on reconnect: restore nickname, room, and call based on intent refs
      const wasConnectedBefore = hasHadFirstConnectRef.current;
      hasHadFirstConnectRef.current = true;

      if (wasConnectedBefore) {
        console.log("[useSocket] Reconnected — restoring state", {
          nickname: intentNicknameRef.current,
          roomId: intentRoomIdRef.current,
          callActive: intentCallActiveRef.current,
        });

        if (intentNicknameRef.current) {
          socket.emit("set-nickname", intentNicknameRef.current);
        }

        if (intentRoomIdRef.current) {
          const roomToRejoin = intentRoomIdRef.current;
          socket.emit("join-room", roomToRejoin, (ackData: unknown) => {
            console.log("[useSocket] Auto-rejoin room ack", ackData);
            setRoomId(roomToRejoin);

            // If user was in a call, re-issue call-start (server will join existing active call if present)
            if (intentCallActiveRef.current) {
              socket.emit(
                "call-start",
                { roomId: roomToRejoin },
                (response: { success: boolean; callId?: string; participants?: CallUser[]; error?: string }) => {
                  if (response.success && response.callId) {
                    console.log("[useSocket] Auto-rejoin call success", response.callId, "participants:", response.participants?.length ?? 0);
                    callIdRef.current = response.callId;
                    setCallId(response.callId);
                    // Notify the consumer so it can rebuild stale peer
                    // connections with the fresh roster from the server.
                    handlersRef.current.onReconnectedToCall?.({
                      callId: response.callId,
                      participants: response.participants || [],
                    });
                  } else {
                    console.warn("[useSocket] Auto-rejoin call failed", response.error);
                  }
                }
              );
            }
          });
        }
      }
    });

    socket.on("disconnect", (reason) => {
      console.log("[useSocket] Disconnected", reason);
      setStatus("disconnected");
      // Do NOT clear roomId/callId/intent — they represent user's intent to be in that room/call.
      // They will be restored on reconnect via auto-rejoin in the 'connected' handler above.
      // socket.id becomes invalid but we clear userId since the new socket will have a new id.
      setUserId(null);
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      setStatus("error");
    });

    // Call events
    socket.on("call-started", (data: { callId: string; roomId: string; startedBy: CallUser }) => {
      setCallId(data.callId);
      callIdRef.current = data.callId;
      handlersRef.current.onCallStarted?.(data);
    });

    socket.on("call-user-joined", (data: { callId: string; user: CallUser; participantCount: number }) => {
      handlersRef.current.onUserJoined?.(data);
    });

    socket.on("call-user-left", (data: { callId: string; user: CallUser; remainingCount: number }) => {
      handlersRef.current.onUserLeft?.(data);
    });

    socket.on("call-ended", (data: { callId: string; reason: string }) => {
      setCallId(null);
      callIdRef.current = null;
      handlersRef.current.onCallEnded?.(data);
    });

    socket.on("call-signal", (data: { from: string; fromNickname: string; signalType: string; data: unknown }) => {
      handlersRef.current.onSignal?.(data);
    });

    socket.on("call-media-changed", (data: { user: CallUser; mediaType: string; enabled: boolean }) => {
      handlersRef.current.onMediaChanged?.(data);
    });
  }, [token]);

  const disconnect = useCallback(() => {
    // User-initiated disconnect — clear all intent so we don't auto-rejoin
    intentRoomIdRef.current = null;
    intentCallActiveRef.current = false;
    intentNicknameRef.current = null;
    hasHadFirstConnectRef.current = false;
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setStatus("disconnected");
      setRoomId(null);
      setCallId(null);
      callIdRef.current = null;
      setUserId(null);
    }
  }, []);

  const setNickname = useCallback((nickname: string) => {
    intentNicknameRef.current = nickname;
    if (socketRef.current?.connected) {
      socketRef.current.emit("set-nickname", nickname);
    }
  }, []);

  const joinRoom = useCallback((room: string, ack?: (data: unknown) => void) => {
    intentRoomIdRef.current = room;
    if (socketRef.current?.connected) {
      if (typeof ack === "function") {
        socketRef.current.emit("join-room", room, (data: unknown) => ack(data));
      } else {
        socketRef.current.emit("join-room", room);
      }
      setRoomId(room);
    }
  }, []);

  const leaveRoom = useCallback(() => {
    // User-initiated leave — clear intent so we don't auto-rejoin
    intentRoomIdRef.current = null;
    intentCallActiveRef.current = false;
    if (socketRef.current?.connected && roomId) {
      socketRef.current.emit("leave-room", roomId);
      setRoomId(null);
    }
  }, [roomId]);

  const startCall = useCallback((room: string): Promise<{ callId: string; participants: CallUser[] } | null> => {
    return new Promise((resolve) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit("call-start", { roomId: room }, (response: { success: boolean; callId?: string; participants?: CallUser[]; error?: string }) => {
          if (response.success && response.callId) {
            callIdRef.current = response.callId;
            setCallId(response.callId);
            intentCallActiveRef.current = true;
            resolve({ callId: response.callId, participants: response.participants || [] });
          } else {
            console.error("Failed to start call:", response.error);
            resolve(null);
          }
        });
      } else {
        resolve(null);
      }
    });
  }, []);

  const leaveCall = useCallback(() => {
    // User-initiated leave call — clear intent
    intentCallActiveRef.current = false;
    const activeCallId = callIdRef.current;
    if (socketRef.current?.connected && activeCallId) {
      socketRef.current.emit("call-leave", { callId: activeCallId });
      setCallId(null);
      callIdRef.current = null;
    }
  }, []);

  const sendSignal = useCallback((to: string, signalType: CallSignalType, data: unknown) => {
    const activeCallId = callIdRef.current;
    if (socketRef.current?.connected && activeCallId) {
      socketRef.current.emit("call-signal-peer", { callId: activeCallId, to, signalType, data });
    }
  }, []);

  const toggleMedia = useCallback((mediaType: "audio" | "video", enabled: boolean) => {
    const activeCallId = callIdRef.current;
    if (socketRef.current?.connected && activeCallId) {
      socketRef.current.emit("call-media-toggle", { callId: activeCallId, mediaType, enabled });
    }
  }, []);

  useEffect(() => {
    if (autoConnect && token) {
      connect(token);
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, token, connect, disconnect]);

  return {
    socket: socketRef.current,
    status,
    roomId,
    callId,
    userId,
    iceServers,
    connect,
    disconnect,
    setNickname,
    joinRoom,
    leaveRoom,
    startCall,
    leaveCall,
    sendSignal,
    toggleMedia,
  };
}
